import { useCallback } from "react";

import {
  ClientUserSsoProviderType,
  MemberRol,
} from "../../Types-DTOs/ClientUser";
import { ContractStatus } from "../../Types-DTOs/Contracts";
import { useHttpMethods } from "./HttpMethods";
import { searchResponseSchema } from "../../Types/Search";
import {
  getBusinessSchema,
  inviteUserSchema,
  loginSchema,
  registerUserSchema,
  removeMemberSchema,
  updateMemberRoleSchema,
  createChatSchema,
  getChatByIdSchema,
  sendMessageSchema,
  sendDocumentSchema,
  getAllChatsSchema,
  useInvitationSchema,
  getInvitationSchema,
  agentLoginIntentSchema,
  agentLoginSchema,
  getPaymentUrlSchema,
  updateAllowedMembersSchema,
  getBillsSchema,
  getCancelUrlSchema,
  getCustomerPortalUrlSchema,
  getPaymentCustomerSessionSchema,
  getDetailedSubscriptionSchema,
  getInvitesSchema,
  setMessageFeedbackSchema,
  createDueDiligenceCaseSchema,
  getDueDiligenceCaseSchema,
  listDueDiligenceCasesSchema,
  updateDueDiligenceCaseSchema,
  updateCasePdfSettingsSchema,
  listDueDiligenceReportNotesSchema,
  createDueDiligenceReportNoteSchema,
  updateDueDiligenceReportNoteSchema,
  uploadDueDiligenceDocumentSchema,
  listDueDiligenceDocumentsSchema,
  deleteDueDiligenceDocumentSchema,
  getDocumentSignedUrlSchema,
  getReportFilesSchema,
  getDueDiligencePaymentUrlSchema,
  getDueDiligenceUpsellPaymentUrlSchema,
  getDueDiligenceCreditDiscountSchema,
  applyDueDiligenceCreditDiscountSchema,
  getDueDiligenceUpsellQuoteSchema,
  getReportManifestSchema,
  getReportNumberedSectionSchema,
  translateReportSchema,
  vapidPublicKeySchema,
  getDueDiligencePricingSchema,
  getDueDiligenceChatSuggestionsSchema,
  platformConfigSchema,
  getClientUserSchema,
  getChatCreditsSchema,
  getReferralSchema,
  getCreditTransactionsSchema,
  getDueDiligencePlansSchema,
  getDueDiligencePlanCheckoutSchema,
  getCreditsCheckoutSchema,
  getDueDiligencePlanChangePreviewSchema,
  changeDueDiligencePlanSchema,
  getChatCreditPackPaymentUrlSchema,
  listContractsSchema,
  getContractSchema,
  createContractSchema,
  updateContractVariablesSchema,
  listContractTemplatesSchema,
  suggestAddressesSchema,
  getDueDiligenceFeedbackQuestionsSchema,
  getDueDiligenceFeedbackStatusSchema,
  submitDueDiligenceFeedbackSchema,
} from "./Schemas";
import { CreateDueDiligenceCaseRequest, UpdateDueDiligenceCaseRequest, DocumentType, DueDiligenceTier } from "../../Types-DTOs/DueDiligence";
import { Acquisition } from "../../Utils/acquisition";

export const useApi = () => {
  const { post, get, del, put, postWithFormData, getBlob } = useHttpMethods();

  const registerUserEndpoint = ({
    businessName,
    clientEmail,
    clientPhone,
    clientName,
    clientPassword,
    acquisition,
    referralCode,
    turnstileToken,
  }: {
    businessName: string;
    clientEmail: string;
    clientPhone: string;
    clientName: string;
    clientPassword: string;
    acquisition?: Acquisition;
    referralCode?: string;
    turnstileToken: string;
  }) =>
    post(
      "/client-user/register-with-business",
      {
        businessName,
        clientEmail,
        clientPhone,
        clientName,
        clientPassword,
        acquisition,
        referralCode,
        turnstileToken,
      },
      registerUserSchema
    );

  const registerUserSsoEndpoint = ({
    businessName,
    clientPhone,
    clientName,
    clientSsoProvider,
    clientAuthToken,
    acquisition,
    referralCode,
    turnstileToken,
  }: {
    businessName: string;
    clientPhone: string;
    clientName: string;
    clientSsoProvider: ClientUserSsoProviderType;
    clientAuthToken: string;
    acquisition?: Acquisition;
    referralCode?: string;
    turnstileToken: string;
  }) =>
    post(
      "/client-user/register-with-business",
      {
        businessName,
        clientPhone,
        clientName,
        clientSsoProvider,
        clientAuthToken,
        acquisition,
        referralCode,
        turnstileToken,
      },
      registerUserSchema
    );

  const loginWithPasswordEndpoint = ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) =>
    post(
      "/client-user/login-with-password",
      {
        email,
        password,
      },
      loginSchema
    );

  const loginWithSsoEndpoint = (tokenSso: string) =>
    post(
      "/client-user/login-with-sso",
      {
        tokenSso,
      },
      loginSchema
    );

  const getBusinessesEndpoint = () =>
    get("/business", {}, getBusinessSchema);

  const inviteUserEndpoint = (props: {
    email: string;
    phone: string;
    name: string;
    rol: MemberRol;
  }) => post("/business/invite", props, inviteUserSchema);

  const removeUserEndpoint = (memberId: string) =>
    post("/business/remove-user", { memberId }, removeMemberSchema);

  const updateMemberRoleEndpoint = (memberId: string, rol: MemberRol) =>
    put("/business/update-member-role", { memberId, rol }, updateMemberRoleSchema);

  const createChatEndpoint = useCallback(
    (message: string, agentContext?: Record<string, unknown>) =>
      post("/conversations/create", { message, ...(agentContext ? { agentContext } : {}) }, createChatSchema),
    [post]
  );

  const getChatByIdEndpoint = useCallback(
    (chatId: string) => get(`/conversations/${chatId}`, {}, getChatByIdSchema),
    [get]
  );

  const sendMessagesEndpoint = useCallback(
    (chatId: string, message: string, agentContext?: Record<string, unknown>) =>
      post(`/conversations/send-message`, { chatId, message, ...(agentContext ? { agentContext } : {}) }, sendMessageSchema),
    [post]
  );

  const sendDocumentEndpoint = useCallback(
    (chatId: string | undefined, files: File[], text?: string) => {
      const formData = new FormData();
      if (chatId) {
        formData.append("chatId", chatId);
      }
      if (text) {
        formData.append("text", text);
      }
      files.forEach((file) => formData.append("document", file));
      return postWithFormData(`/conversations/send-document`, formData, sendDocumentSchema);
    },
    [postWithFormData]
  );

  const getAllChatsEndpoint = useCallback(
    () => get(`/conversations/get-all`, {}, getAllChatsSchema),
    [get]
  );

  const setMessageFeedbackEndpoint = useCallback(
    (chatId: string, messageId: string, feedback: 1 | -1 | 0) =>
      put(`/conversations/${chatId}/messages/${messageId}`, { feedback }, setMessageFeedbackSchema),
    [put]
  );

  const useInvitationEndpoint = (props: {
    invitationId: string;
    email: string | undefined;
    password: string | undefined;
  }) => post(`/client-user/use-invitation`, props, useInvitationSchema);

  const useInvitationWithSsoEndpoint = (props: {
    invitationId: string;
    ssoProvider: ClientUserSsoProviderType;
    tokenSso: string;
  }) =>
    post(`/client-user/use-invitation-with-sso`, props, useInvitationSchema);

  const getInvitationEndpoint = (invitationId: string) =>
    get(`/client-user/get-invitation`, { invitationId }, getInvitationSchema);

  const tryAgentLoginEndpoint = async (phone: string) =>
    post(
      "/client-user/agent-login-intent",
      {
        phone,
      },
      agentLoginIntentSchema
    );

  const agentLoginIntentEndpoint = async (loginIntent: string, code: string) =>
    post(
      "/client-user/agent-login",
      { intentId: loginIntent, code },
      agentLoginSchema
    );

  const getPaymentUrlEndpoint = async (mikeUserQuantity: number) =>
    get(
      "/business/get-payment-url",
      {
        quantity: mikeUserQuantity,
      },
      getPaymentUrlSchema
    );

  const getPaymentCustomerSessionEndpoint = async () =>
    get(
      "/business/get-payment-customer-session",
      {},
      getPaymentCustomerSessionSchema,
    );

  const updateAllowedMemberQuantityEndpoint = async (
    mikeUserQuantity: number
  ) =>
    post(
      "/business/update-allowed-members-quantity",
      {
        quantity: mikeUserQuantity,
      },
      updateAllowedMembersSchema
    );

  const getBillsEndpoint = async () =>
    get("/business/get-bills", {}, getBillsSchema);

  const getCancelSubscriptionUrlEndpoint = async () =>
    get("/business/get-cancel-subscription-url", {}, getCancelUrlSchema);

  const getCustomerPortalUrlEndpoint = async () =>
    get("/business/get-customer-portal-url", {}, getCustomerPortalUrlSchema);

  const getDetailedSubscriptionEndpoint = async () => {
    return get("/business/subscription-details", {}, getDetailedSubscriptionSchema);
  }

  const getChatCreditsEndpoint = useCallback(
    () => get("/business/chat-credits", {}, getChatCreditsSchema),
    [get]
  );

  const getReferralEndpoint = useCallback(
    () => get("/business/referral", {}, getReferralSchema),
    [get]
  );

  const getCreditTransactionsEndpoint = useCallback(
    (cursor?: string) =>
      get("/business/credit-transactions", cursor ? { cursor } : {}, getCreditTransactionsSchema),
    [get]
  );

  const getDueDiligencePlansEndpoint = useCallback(
    () => get("/business/due-diligence-plans", {}, getDueDiligencePlansSchema),
    [get]
  );

  const getDueDiligencePlanCheckoutEndpoint = useCallback(
    (lookupKey: string, dueDiligenceCaseId?: string) =>
      post(
        "/business/due-diligence-plans/checkout",
        dueDiligenceCaseId ? { lookupKey, dueDiligenceCaseId } : { lookupKey },
        getDueDiligencePlanCheckoutSchema
      ),
    [post]
  );

  const getCreditsCheckoutEndpoint = useCallback(
    () => post("/business/credits/checkout", {}, getCreditsCheckoutSchema),
    [post]
  );

  const getDueDiligencePlanChangePreviewEndpoint = useCallback(
    (lookupKey: string) =>
      get("/business/due-diligence-plan/preview", { lookupKey }, getDueDiligencePlanChangePreviewSchema),
    [get]
  );

  const changeDueDiligencePlanEndpoint = useCallback(
    (lookupKey: string) => put("/business/due-diligence-plan", { lookupKey }, changeDueDiligencePlanSchema),
    [put]
  );

  const getChatCreditPackPaymentUrlEndpoint = useCallback(
    () => get("/business/get-chat-credit-pack-payment-url", {}, getChatCreditPackPaymentUrlSchema),
    [get]
  );

  const requestPasswordResetEndpoint = async(email: string) =>
    post('/password-reset/request', { email });

  const resetPasswordEndpoint = async(token: string, email: string, newPassword: string) =>
    post('/password-reset/reset', {
      token,
      email,
      newPassword
    });

  const getInvitesEndpoint = useCallback(
    (businessId: string) => get(`/business/${businessId}/invites`, {}, getInvitesSchema),
    [get]
  );

  const deleteInvite = (businessId: string, inviteId: string) =>
    del(`/business/${businessId}/invite/${inviteId}`, {});

  // Due Diligence endpoints
  const createDueDiligenceCaseEndpoint = (data: CreateDueDiligenceCaseRequest) =>
    post("/due-diligence/cases", data, createDueDiligenceCaseSchema);

  const getDueDiligenceCaseEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}`, {}, getDueDiligenceCaseSchema);

  const listDueDiligenceCasesEndpoint = (filters?: { status?: string; includeArchived?: boolean }) =>
    get("/due-diligence/cases", filters, listDueDiligenceCasesSchema);

  const updateDueDiligenceCaseEndpoint = (caseId: string, data: UpdateDueDiligenceCaseRequest) =>
    put(`/due-diligence/cases/${caseId}`, data, updateDueDiligenceCaseSchema);

  const saveHiddenPdfSectionsEndpoint = (caseId: string, hiddenSections: string[]) =>
    put(
      `/due-diligence/cases/${caseId}/pdf-settings`,
      { hiddenSections },
      updateCasePdfSettingsSchema,
    );

  // Document endpoints
  const uploadDueDiligenceDocumentEndpoint = (caseId: string, file: File, documentType: DocumentType) => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('documentType', documentType);
    return postWithFormData(`/due-diligence/cases/${caseId}/documents`, formData, uploadDueDiligenceDocumentSchema);
  };

  const listDueDiligenceDocumentsEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}/documents`, {}, listDueDiligenceDocumentsSchema);

  const getDocumentSignedUrlEndpoint = (caseId: string, documentId: string) =>
    get(`/due-diligence/cases/${caseId}/documents/${documentId}/signed-url`, {}, getDocumentSignedUrlSchema);

  const deleteDueDiligenceDocumentEndpoint = (caseId: string, documentId: string) =>
    del(`/due-diligence/cases/${caseId}/documents/${documentId}`, {}, deleteDueDiligenceDocumentSchema);

  const listDueDiligenceReportNotesEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}/notes`, {}, listDueDiligenceReportNotesSchema);

  const createDueDiligenceReportNoteEndpoint = (caseId: string, sectionNumber: number, content: string) =>
    post(`/due-diligence/cases/${caseId}/notes`, { sectionNumber, content }, createDueDiligenceReportNoteSchema);

  const updateDueDiligenceReportNoteEndpoint = (caseId: string, noteId: string, content: string) =>
    put(`/due-diligence/cases/${caseId}/notes/${noteId}`, { content }, updateDueDiligenceReportNoteSchema);

  // 204, no body: nothing to validate, so no schema.
  const deleteDueDiligenceReportNoteEndpoint = (caseId: string, noteId: string) =>
    del(`/due-diligence/cases/${caseId}/notes/${noteId}`, {});

  const getDueDiligencePaymentUrlEndpoint = (caseId: string, tier: DueDiligenceTier) =>
    get(`/due-diligence/cases/${caseId}/payment-url`, { tier }, getDueDiligencePaymentUrlSchema);

  const getDueDiligenceUpsellPaymentUrlEndpoint = (caseId: string, toTier: DueDiligenceTier, promotionCode?: string) =>
    post(`/due-diligence/cases/${caseId}/upsell-payment-url`, { toTier, promotionCode }, getDueDiligenceUpsellPaymentUrlSchema);

  const getDueDiligenceUpsellQuoteEndpoint = (caseId: string, toTier: DueDiligenceTier, promotionCode?: string) =>
    get(`/due-diligence/cases/${caseId}/upsell-quote`, { toTier, promotionCode }, getDueDiligenceUpsellQuoteSchema);

  const verifyDueDiligencePaymentEndpoint = (caseId: string, sessionId: string) =>
    post(`/due-diligence/cases/${caseId}/verify-payment`, { sessionId });

  const getDueDiligenceCreditDiscountEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}/credit-discount`, {}, getDueDiligenceCreditDiscountSchema);

  const applyDueDiligenceCreditDiscountEndpoint = (caseId: string) =>
    post(`/due-diligence/cases/${caseId}/credit-discount`, {}, applyDueDiligenceCreditDiscountSchema);

  const payDueDiligenceCaseWithCreditsEndpoint = (caseId: string) =>
    post(`/due-diligence/cases/${caseId}/pay-with-credits`, {}, getDueDiligenceCaseSchema);

  const upgradeDueDiligenceCaseWithCreditsEndpoint = (caseId: string, toTier: DueDiligenceTier) =>
    post(`/due-diligence/cases/${caseId}/upgrade-with-credits`, { toTier }, getDueDiligenceCaseSchema);

  const getReportManifestEndpoint = (caseId: string, language?: string) =>
    get(
      `/due-diligence/cases/${caseId}/report/manifest`,
      language ? { language } : {},
      getReportManifestSchema,
    )

  const getReportNumberedSectionEndpoint = (
    caseId: string,
    sectionNumber: string,
    language?: string,
  ) =>
    get(
      `/due-diligence/cases/${caseId}/report/sections/${sectionNumber}`,
      language ? { language } : {},
      getReportNumberedSectionSchema,
    )

  const translateReportEndpoint = (caseId: string, language: string) =>
    post(`/due-diligence/cases/${caseId}/report/translate`, { language }, translateReportSchema)

  const getReportFilesEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}/report-files`, {}, getReportFilesSchema);

  // The PDF is another representation of the report, not another resource: the
  // same URL as the report itself, and `Accept: application/pdf` picks it.
  const exportDueDiligencePdfEndpoint = (caseId: string, language?: string, excludedSections: string[] = []) =>
    getBlob(
      `/due-diligence/cases/${caseId}/report`,
      { ...(language ? { language } : {}), ...(excludedSections.length ? { excludedSections: excludedSections.join(',') } : {}) },
      { Accept: 'application/pdf' },
    )

  const getDueDiligencePricingEndpoint = () =>
    get(`/due-diligence/cases/pricing`, {}, getDueDiligencePricingSchema);

  const getDueDiligenceChatSuggestionsEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}/chat-suggestions`, {}, getDueDiligenceChatSuggestionsSchema);

  // Search
  const searchEndpoint = (params: { q: string; type?: string; limit?: number; offset?: number }) =>
    get('/search', params, searchResponseSchema)

  // Due Diligence Feedback
  const getDueDiligenceFeedbackQuestionsEndpoint = () =>
    get(`/due-diligence/feedback/questions`, {}, getDueDiligenceFeedbackQuestionsSchema);

  const getDueDiligenceFeedbackStatusEndpoint = (caseId: string) =>
    get(`/due-diligence/cases/${caseId}/feedback`, {}, getDueDiligenceFeedbackStatusSchema);

  const submitDueDiligenceFeedbackEndpoint = (
    caseId: string,
    data: { npsScore: number; answers: Record<string, string> }
  ) => post(`/due-diligence/cases/${caseId}/feedback`, data, submitDueDiligenceFeedbackSchema);

  const dismissDueDiligenceFeedbackPromptEndpoint = () =>
    post(`/due-diligence/feedback/dismiss`);

  // Push Notifications
  const getVapidPublicKeyEndpoint = () =>
    get("/push-subscription/vapid-public-key", {}, vapidPublicKeySchema);

  const subscribePushEndpoint = (subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) =>
    post("/push-subscription/subscribe", subscription);

  const unsubscribePushEndpoint = (endpoint: string) =>
    del("/push-subscription/unsubscribe", { endpoint });

  // Platform Config
  const getPlatformConfigEndpoint = useCallback(
    () => get(`/platform-config`, {}, platformConfigSchema),
    [get]
  );

  const getClientUserEndpoint = useCallback(
    () => get("/client-user", {}, getClientUserSchema),
    [get]
  );

  const updateClientUserEndpoint = useCallback(
    (data: { completedTours: string[] }) => put("/client-user", data),
    [put]
  );

  // Contracts
  const listContractsEndpoint = (filters?: { includeArchived?: boolean }) =>
    get("/contracts", filters, listContractsSchema);

  const getContractEndpoint = (contractId: string) =>
    get(`/contracts/${contractId}`, {}, getContractSchema);

  const createContractEndpoint = (data: {
    templateId: string;
    title: string;
  }) => post("/contracts", data, createContractSchema);

  const updateContractVariablesEndpoint = (contractId: string, variables: Record<string, string>) =>
    put(`/contracts/${contractId}/variables`, { variables }, updateContractVariablesSchema);

  const updateContractMetadataEndpoint = (
    contractId: string,
    data: { title?: string; status?: ContractStatus }
  ) => put(`/contracts/${contractId}`, data, getContractSchema);

  const exportContractDocxEndpoint = (contractId: string) =>
    getBlob(`/contracts/${contractId}`, {}, {
      Accept: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

  const listContractTemplatesEndpoint = () =>
    get("/contract-templates", {}, listContractTemplatesSchema);

  const suggestAddressesEndpoint = useCallback(
    (q: string) => get("/addresses", { q }, suggestAddressesSchema),
    [get]
  );

  return {
    registerUserEndpoint,
    registerUserSsoEndpoint,
    loginWithPasswordEndpoint,
    loginWithSsoEndpoint,
    getBusinessesEndpoint,
    inviteUserEndpoint,
    removeUserEndpoint,
    updateMemberRoleEndpoint,
    createChatEndpoint,
    getChatByIdEndpoint,
    sendMessagesEndpoint,
    sendDocumentEndpoint,
    getAllChatsEndpoint,
    setMessageFeedbackEndpoint,
    useInvitationEndpoint,
    useInvitationWithSsoEndpoint,
    getInvitationEndpoint,
    tryAgentLoginEndpoint,
    agentLoginIntentEndpoint,
    getPaymentUrlEndpoint,
    getPaymentCustomerSessionEndpoint,
    updateAllowedMemberQuantityEndpoint,
    getBillsEndpoint,
    getCancelSubscriptionUrlEndpoint,
    getCustomerPortalUrlEndpoint,
    getDetailedSubscriptionEndpoint,
    getChatCreditsEndpoint,
    getReferralEndpoint,
    getCreditTransactionsEndpoint,
    getDueDiligencePlansEndpoint,
    getDueDiligencePlanCheckoutEndpoint,
    getCreditsCheckoutEndpoint,
    getDueDiligencePlanChangePreviewEndpoint,
    changeDueDiligencePlanEndpoint,
    getChatCreditPackPaymentUrlEndpoint,
    requestPasswordResetEndpoint,
    resetPasswordEndpoint,
    getInvitesEndpoint,
    deleteInvite,
    // Due Diligence
    createDueDiligenceCaseEndpoint,
    getDueDiligenceCaseEndpoint,
    listDueDiligenceCasesEndpoint,
    updateDueDiligenceCaseEndpoint,
    payDueDiligenceCaseWithCreditsEndpoint,
    getDueDiligenceCreditDiscountEndpoint,
    applyDueDiligenceCreditDiscountEndpoint,
    upgradeDueDiligenceCaseWithCreditsEndpoint,
    saveHiddenPdfSectionsEndpoint,
    // Due Diligence Documents
    uploadDueDiligenceDocumentEndpoint,
    listDueDiligenceDocumentsEndpoint,
    getDocumentSignedUrlEndpoint,
    deleteDueDiligenceDocumentEndpoint,
    listDueDiligenceReportNotesEndpoint,
    createDueDiligenceReportNoteEndpoint,
    updateDueDiligenceReportNoteEndpoint,
    deleteDueDiligenceReportNoteEndpoint,
    getDueDiligencePaymentUrlEndpoint,
    getDueDiligenceUpsellPaymentUrlEndpoint,
    getDueDiligenceUpsellQuoteEndpoint,
    verifyDueDiligencePaymentEndpoint,
    // Due Diligence Report
    getReportManifestEndpoint,
    getReportNumberedSectionEndpoint,
    translateReportEndpoint,
    getReportFilesEndpoint,
    exportDueDiligencePdfEndpoint,
    getDueDiligencePricingEndpoint,
    getDueDiligenceChatSuggestionsEndpoint,
    getDueDiligenceFeedbackQuestionsEndpoint,
    getDueDiligenceFeedbackStatusEndpoint,
    submitDueDiligenceFeedbackEndpoint,
    dismissDueDiligenceFeedbackPromptEndpoint,
    // Contracts
    listContractsEndpoint,
    getContractEndpoint,
    createContractEndpoint,
    updateContractVariablesEndpoint,
    updateContractMetadataEndpoint,
    exportContractDocxEndpoint,
    listContractTemplatesEndpoint,
    suggestAddressesEndpoint,
    // Search
    searchEndpoint,
    // Push Notifications
    getVapidPublicKeyEndpoint,
    subscribePushEndpoint,
    unsubscribePushEndpoint,
    // Platform Config
    getPlatformConfigEndpoint,
    getClientUserEndpoint,
    updateClientUserEndpoint,
  };
};
